import { useState } from "react";
import { toast } from "sonner";
import { Languages, Pencil, RefreshCcw } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { LANGS, useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { translateError } from "@/i18n/errors";

const BADGE = { ok: "bg-emerald-600/80 text-white", pending: "bg-amber-500/80 text-black", failed: "bg-red-700 text-white" };
const STATUS_KEY = { ok: "tr.ok", pending: "tr.pending", failed: "tr.failed" };

const Row = ({ versionId, code, label, tr, onSaved }) => {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(tr.text || "");
  const { base } = useSpace();
  const save = async () => {
    try {
      const { data } = await api.put(`${base}/versions/${versionId}/description/translations/${code}`, { text });
      toast.success(t("tr.saved"));
      setEditing(false);
      onSaved(data);
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <li className="rounded-xl border border-[rgba(245,197,66,0.15)] bg-black/30 p-3" data-testid={`translation-row-${code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-[5.5rem] text-start text-sm font-semibold text-white">{label}</span>
        <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase ${BADGE[tr.status]}`} data-testid={`translation-status-${code}`}>{t(STATUS_KEY[tr.status])}</span>
        {tr.manual && <span className="rounded-full border border-[var(--gold)] px-2 py-0.5 text-[0.65rem] uppercase text-[var(--gold)]" data-testid={`translation-manual-${code}`}>{t("tr.manual")}</span>}
        <button type="button" className="ms-auto inline-flex items-center gap-1 text-xs text-[var(--gold)] hover:text-[var(--gold-bright)]" onClick={() => { setText(tr.text || ""); setEditing((e) => !e); }} data-testid={`translation-edit-btn-${code}`}>
          <Pencil className="h-3.5 w-3.5" /> {t("tr.edit")}
        </button>
      </div>
      {editing && (
        <div className="mt-3 space-y-2">
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} dir={code === "ar" ? "rtl" : "ltr"} data-testid={`translation-textarea-${code}`} />
          <button type="button" className="btn-gold btn-sm" onClick={save} disabled={!text.trim()} data-testid={`translation-save-btn-${code}`}>{t("tr.save")}</button>
        </div>
      )}
    </li>
  );
};

export const TranslationsPanel = ({ versionId, desc, onChange }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const retry = async () => {
    try {
      const { data } = await api.post(`${base}/versions/${versionId}/description/retranslate`);
      toast.success(t("tr.retrying"));
      onChange(data);
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <section className="space-y-3" data-testid="translations-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-wider text-white"><Languages className="h-4 w-4 text-[var(--gold)]" /> {t("tr.title")}</h3>
        <button type="button" className="btn-red btn-sm" onClick={retry} data-testid="translations-retry-btn"><RefreshCcw className="h-4 w-4" /> {t("tr.retry")}</button>
      </div>
      <p className="text-xs text-neutral-500">{t("tr.fallbackNote")}</p>
      <ul className="space-y-2">
        {LANGS.filter((l) => l.code !== "fr" && desc.translations?.[l.code]).map((l) => (
          <Row key={`${l.code}-${desc.translations[l.code].updated_at}`} versionId={versionId} code={l.code} label={l.label} tr={desc.translations[l.code]} onSaved={onChange} />
        ))}
      </ul>
    </section>
  );
};
